import { useMemo } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { getSelectedCtiContactByNumberFromIntegration } from 'flow/selectors/cti-integration-selectors';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import { CallRecord } from 'flow/types/CallRecord';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';

export const useCallRecordWithCtiContact = (
    callRecord: TypedMap<CallRecord> | undefined,
): TypedMap<CallRecord> | undefined => {
    const number = callRecord?.getIn(['number', 'number']);

    const integrationContact = useAppSelector((state) =>
        number
            ? getSelectedCtiContactByNumberFromIntegration(
                  state.integration,
                  number,
              )
            : undefined,
    );

    return useMemo(() => {
        if (
            !callRecord ||
            !integrationContact ||
            integrationContact.isEmpty()
        ) {
            return callRecord;
        }
        return callRecord.set('contact', integrationContact);
    }, [callRecord, integrationContact]);
};

/**
 * Same as {@link useCallRecordWithCtiContact} but selects the call record by
 * key first. Use this in components that only have a `callRecordKey` so the
 * contact they render (name, avatar, source icon) gets the CTI overlay applied
 * consistently — CTI contacts take precedence over contact-lookup contacts.
 */
export const useCallRecordWithCtiContactByKey = (
    callRecordKey: CallRecordKey | undefined,
): TypedMap<CallRecord> | undefined => {
    const rawCallRecord = useAppSelector((state) =>
        callRecordKey
            ? selectCallRecordFromKey(state, callRecordKey)
            : undefined,
    );
    return useCallRecordWithCtiContact(rawCallRecord);
};
